import { TicketingTable } from '@services/member/ticketing/ticketing.types';
import { formatDate } from '@utils/dates';
import { formatMoney } from '@utils/numbers';

const PRINT_STYLES = `
    body { 
        font-family: Arial, sans-serif; 
        margin: 12px; 
        font-size: 10px; 
        color: #000; 
        line-height: 1.2;
    }
    
    h1 { 
        text-align: center; 
        border-bottom: 2px solid #000; 
        padding-bottom: 6px; 
        margin-bottom: 12px; 
        font-size: 15px;
    }
    
    .summary {
        margin-bottom: 20px;
    }
    
    .summary-table {
        width: 50%;
        margin: 0 auto 20px auto;
        border-collapse: collapse;
        font-size: 11px;
    }
    
    .summary-table th {
        background: #2c3e50;
        color: white;
        padding: 8px 12px;
        text-align: center;
        font-weight: bold;
        border: 1px solid #34495e;
    }
    
    .summary-table td {
        padding: 8px 12px;
        text-align: center;
        border: 1px solid #bdc3c7;
        background: #ecf0f1;
        font-weight: bold;
        color: #2c3e50;
    }
    
    .summary-table .label-cell {
        text-align: left;
        background: #f8f9fa;
        font-weight: bold;
        color: #495057;
    }
    
    .summary-table .value-cell {
        text-align: right;
        background: white;
        color: #2c3e50;
        font-weight: bold;
    }
    
    table { 
        width: 100%; 
        border-collapse: collapse; 
        margin: 5px 0; 
        font-size: 9px;
    }
    
    th, td { 
        border: 1px solid #ccc; 
        padding: 4px 6px; 
        text-align: left; 
        vertical-align: top;
        line-height: 1.2;
    }
    
    th { 
        background: #f0f0f0; 
        font-weight: bold; 
        font-size: 9px;
    }
    
    .text-center {
        text-align: center;
    }
    
    .text-right {
        text-align: right;
    }
    
    @page { 
        margin: 1cm; 
        size: A4 landscape;
    }
    
    @media print {
        .no-print {
            display: none;
        }
    }
`;

export const generatePrintHTML = (tickets: TicketingTable[]): string => {
    const totalBillets = tickets.reduce((sum, ticket) => sum + ticket.saleCount, 0);
    const totalMontant = tickets.reduce((sum, ticket) => sum + ticket.saleAmount, 0);

    const generateSummary = () => `
        <div class="summary">
            <table class="summary-table">
                <thead>
                    <tr>
                        <th colspan="2">RÉSUMÉ DE LA SÉLECTION</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="label-cell">Nombre de ventes</td>
                        <td class="value-cell">${tickets.length}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Total billets vendus</td>
                        <td class="value-cell">${totalBillets}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Montant total</td>
                        <td class="value-cell">${formatMoney(totalMontant)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;

    const generateTicketsTable = () => `
        <table>
            <thead>
                <tr>
                    <th>Adhérent</th>
                    <th>N° COSEM</th>
                    <th>Type de billet</th>
                    <th>Date de vente</th>
                    <th class="text-center">Nb billets</th>
                    <th class="text-right">Montant</th>
                    <th class="text-center">Numéros</th>
                </tr>
            </thead>
            <tbody>
                ${tickets
                    .map(
                        (ticket) => `
                    <tr>
                        <td>${ticket.member?.fullname || 'N/A'}</td>
                        <td class="text-center">${ticket.member?.cosemNumber || 'N/A'}</td>
                        <td>${ticket.ticketType.label}</td>
                        <td class="text-center">${formatDate(ticket.ticketingAt)}</td>
                        <td class="text-center">${ticket.saleCount}</td>
                        <td class="text-right">${formatMoney(ticket.saleAmount)}</td>
                        <td class="text-center">
                            ${
                                ticket.firstTicketNumber === ticket.lastTicketNumber
                                    ? ticket.firstTicketNumber
                                    : `${ticket.firstTicketNumber} - ${ticket.lastTicketNumber}`
                            }
                        </td>
                    </tr>
                `
                    )
                    .join('')}
            </tbody>
        </table>
    `;

    return `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>Billets sélectionnés - ${tickets.length} vente(s)</title>
            <style>${PRINT_STYLES}</style>
        </head>
        <body>
            <h1>LISTE DES BILLETS SÉLECTIONNÉS</h1>
            ${generateSummary()}
            ${generateTicketsTable()}
        </body>
        </html>
    `;
};
